if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.modalContent.removeAttribute('aria-busy');
        this.topferoQuickAddController?.abort();
        this.topferoVariantUnsubscriber?.();
        this.topferoVariantUnsubscriber = null;

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');
        this.renderTopferoLoadingSkeleton();
        super.show(opener);

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) throw new Error('Quick add product content was not found');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.modalContent.removeAttribute('aria-busy');
            this.enhanceTopferoQuickAdd();
            requestAnimationFrame(() => this.updateTopferoSubmitPrice());
            window.setTimeout(() => this.updateTopferoSubmitPrice(), 250);

            if (window.ProductModel) window.ProductModel.loadShopifyXR();
          })
          .catch((error) => {
            console.error(error);
            this.hide(true);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeTopferoBrandText(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.focusTopferoPurchaseContent(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        productElement.querySelectorAll(
          [
            'shopify-payment-terms',
            'payment-terms',
            '.tf-pdp-payment-strip',
            '.product__text',
            '.product__description',
            '.product__accordion',
            '.product__tax',
            '.product__sku',
            '.product-popup-modal__opener',
            '.share-button',
            'share-button',
            '.tf-pdp-rating',
            '.rating-wrapper',
            '.tf-pdp-title-badges',
            '.tf-pdp-support',
            '.tf-pdp-benefits',
            '.tf-pdp-detail-grid',
            '.tf-pdp-tabs-shell',
            '.tf-pdp-workshop',
            '.tf-pdp-inspirations',
            '.tf-breadcrumb',
            '.complementary-products',
            'product-recommendations',
            '.rte',
            '.subtitle',
            '.tf-pdp-summary',
            '.tf-pdp-category',
            '.product__vendor',
            '.caption-with-letter-spacing'
          ].join(', ')
        ).forEach((element) => element.remove());

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      focusTopferoPurchaseContent(productElement) {
        productElement.dataset.quickAddFocused = 'true';
        productElement.classList.add('tf-quick-add-product');

        const product = productElement.querySelector('.product');
        product?.classList.add('tf-quick-add-product__layout');

        const infoContainer = productElement.querySelector('.product__info-container');
        infoContainer?.classList.add('tf-quick-add-product__info');

        const title = productElement.querySelector('.product__title h1, .product__title h2');
        const modalLabel = this.querySelector('[role="dialog"]')?.getAttribute('aria-label');
        if (title && modalLabel) title.setAttribute('aria-label', modalLabel);
      }

      renderTopferoLoadingSkeleton() {
        if (!this.modalContent) return;

        this.modalContent.setAttribute('aria-busy', 'true');
        this.modalContent.innerHTML = `
          <div class="tf-quick-add-skeleton" aria-hidden="true">
            <div class="tf-quick-add-skeleton__media">
              <div class="tf-quick-add-skeleton__media-inner"></div>
            </div>
            <div class="tf-quick-add-skeleton__body">
              <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--title"></span>
              <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--title-2"></span>
              <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--price"></span>
              
              <div class="tf-quick-add-skeleton__variants">
                <span class="tf-quick-add-skeleton__variant"></span>
                <span class="tf-quick-add-skeleton__variant"></span>
                <span class="tf-quick-add-skeleton__variant"></span>
              </div>

              <div class="tf-quick-add-skeleton__actions">
                <span class="tf-quick-add-skeleton__button tf-quick-add-skeleton__button--qty"></span>
                <span class="tf-quick-add-skeleton__button tf-quick-add-skeleton__button--add"></span>
              </div>
              
              <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--payment"></span>

              <div class="tf-quick-add-skeleton__benefits">
                <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--benefit"></span>
                <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--benefit"></span>
                <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--benefit"></span>
              </div>

              <span class="tf-quick-add-skeleton__line tf-quick-add-skeleton__line--details"></span>
            </div>
          </div>
        `;
      }

      removeTopferoBrandText(productElement) {
        productElement.querySelectorAll('.product__title h1, .product__title h2').forEach((title) => {
          title.textContent = title.textContent.trim().replace(/^(TopFero|MetaloPro)\s+/i, '');
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      enhanceTopferoQuickAdd() {
        this.topferoQuickAddController?.abort();
        this.topferoQuickAddController = new AbortController();

        this.injectTopferoPreviewImage();
        this.initializeTopferoPaymentButtons();
        this.updateTopferoSubmitPrice();
        this.modalContent.addEventListener('change', () => this.scheduleTopferoSubmitPriceUpdate(), {
          signal: this.topferoQuickAddController.signal,
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.topferoVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () =>
            this.scheduleTopferoSubmitPriceUpdate()
          );
        }
      }

      injectTopferoPreviewImage() {
        const wrapper = this.modalContent.querySelector('.product__media-wrapper');
        if (!wrapper || wrapper.querySelector('.tf-quick-add-modal__image-frame')) return;

        const sourceImage = wrapper.querySelector('.product__media img, .product-media-container img, img');
        if (!sourceImage) return;

        const frame = document.createElement('div');
        frame.className = 'tf-quick-add-modal__image-frame';

        const image = sourceImage.cloneNode(false);
        image.className = 'tf-quick-add-modal__image';
        image.loading = 'eager';
        image.decoding = 'async';

        frame.append(image);
        wrapper.prepend(frame);
      }

      initializeTopferoPaymentButtons() {
        window.requestAnimationFrame(() => {
          if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
        });
      }

      scheduleTopferoSubmitPriceUpdate() {
        this.updateTopferoSubmitPrice();
        window.setTimeout(() => this.updateTopferoSubmitPrice(), 250);
        window.setTimeout(() => this.updateTopferoSubmitPrice(), 700);
      }

      updateTopferoSubmitPrice() {
        const submitButton = this.modalContent.querySelector('.product-form__submit');
        if (!submitButton) return;

        const priceElements = Array.from(this.modalContent.querySelectorAll('.price-item')).filter((element) =>
          element.textContent.trim()
        );
        const priceText = priceElements[0]?.textContent.trim();
        if (!priceText) return;

        let priceNode = submitButton.querySelector('.tf-quick-add-submit-price');
        if (!priceNode) {
          priceNode = document.createElement('span');
          priceNode.className = 'tf-quick-add-submit-price';
          submitButton.append(priceNode);
        }
        priceNode.textContent = `· ${priceText}`;
      }
    }
  );
}
